import Vue from "vue";
import VueRouter from "vue-router";
import Layout from "@/components/Layout/index.vue";
import { isCurrentTokenValid, removeToken } from "@/utils/auth";
import store from "@/store";

Vue.use(VueRouter);

// 路由配置
const routes = [
  {
    path: "/sign",
    name: "Gate",
    component: () => import("@/views/Gate/index.vue"),
    hidden: true,
    meta: { title: "验证" },
  },
  {
    path: "/mobile-checkin",
    name: "MobileCheckin",
    component: () => import("@/views/Mobile/MobileCheckin.vue"),
    hidden: true,
    meta: { title: "员工打卡", bypassGate: true },
  },
  {
    path: "/office-location-setup",
    name: "OfficeLocationSetup",
    component: () => import("@/views/Mobile/OfficeLocationSetup.vue"),
    hidden: true,
    meta: { title: "设置公司坐标", bypassGate: true },
  },
  {
    path: "/mobile-upload/:token",
    name: "MobilePhotoUpload",
    component: () => import("@/views/Mobile/MobilePhotoUpload.vue"),
    hidden: true,
    meta: { title: "寄样照片上传", bypassGate: true },
  },
  {
    path: "/redirect/:path*",
    hidden: true,
    component: () => import("@/views/Redirect/index.vue"),
    meta: { bypassGate: true },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/Login/index.vue"),
    hidden: true,
    meta: { title: "登录" },
  },
  {
    path: "/",
    component: Layout,
    hidden: true,
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      const role = store.state.user.userInfo?.role;
      if (role === 'staff') {
        next("/my-tasks");
      } else {
        next("/dashboard");
      }
    },
  },
  {
    path: "/dashboard",
    component: Layout,
    meta: {
      title: "首页",
      icon: "el-icon-s-home",
      affix: true,
      requiresAuth: true,
      roles: ['super_admin', 'operator'],
    },
    children: [
      {
        path: "",
        name: "Dashboard",
        component: () => import("@/views/Dashboard/index.vue"),
        meta: {
          title: "首页",
        },
      },
    ],
  },
  {
    path: "/order",
    component: Layout,
    redirect: "/order/list",
    meta: {
      title: "订单中心",
      icon: "el-icon-s-order",
      requiresAuth: true,
      permissions: ["orders:read", "cutting_board", "samples", "patch_fabric"],
    },
    children: [
      {
        path: "cutting",
        name: "CuttingWorkbench",
        component: () => import("@/views/Business/CuttingBoard/index.vue"),
        meta: {
          title: "裁布工作台",
          icon: "el-icon-scissors",
          noCache: true,
          permission: "cutting_board",
        },
      },
      {
        path: "list",
        name: "OrderList",
        component: () => import("@/views/Business/Order/index.vue"),
        meta: {
          title: "订单列表",
          icon: "el-icon-s-order",
          noCache: false,
          permission: "orders:read",
        },
      },
      {
        path: "custom",
        name: "CustomOrderList",
        hidden: true,
        component: () => import("@/views/Business/CustomOrder/index.vue"),
        meta: {
          title: "定制订单",
          icon: "el-icon-s-ticket",
          noCache: false,
          permission: "orders:read",
        },
      },
      {
        path: "sample",
        name: "SampleManage",
        hidden: true,
        component: () => import("@/views/Business/Sample/index.vue"),
        meta: {
          title: "小样管理",
          icon: "el-icon-postcard",
          noCache: false,
          permission: "samples",
        },
      },
      {
        path: "fabric-blocks",
        name: "FabricBlockManage",
        hidden: true,
        component: () => import("@/views/Business/FabricBlock/index.vue"),
        meta: {
          title: "布块管理",
          icon: "el-icon-receiving",
          noCache: false,
          permission: "patch_fabric",
        },
      },
      {
        path: "print-records",
        name: "PrintRecords",
        component: () => import("@/views/Business/PrintRecords/index.vue"),
        meta: {
          title: "打印记录",
          icon: "el-icon-printer",
          noCache: false,
        },
      },
      {
        path: "sheet",
        name: "OrderSheet",
        hidden: true,
        component: () => import("@/views/Business/OrderSheet/index.vue"),
        meta: {
          title: "单据生成",
          icon: "el-icon-document",
          noCache: true,
          permission: "orders:read",
        },
      },
    ],
  },
  {
    path: "/inventory",
    component: Layout,
    redirect: "/inventory/instock",
    meta: {
      title: "库存管理",
      icon: "el-icon-box",
      requiresAuth: true,
      permission: "inventory:read",
    },
    children: [
      {
        path: "instock",
        name: "InstockList",
        component: () => import("@/views/Business/Instock/index.vue"),
        meta: {
          title: "现货列表",
          icon: "el-icon-box",
          noCache: false,
        },
      },
      {
        path: "presale",
        name: "PresaleList",
        hidden: true,
        component: () => import("@/views/Business/Presale/index.vue"),
        meta: {
          title: "预售列表",
          icon: "el-icon-time",
          noCache: false,
        },
      },
      {
        path: "presale-groups",
        name: "PresaleGroups",
        hidden: true,
        component: () => import("@/views/Business/PresaleGroups/index.vue"),
        meta: {
          title: "预售管理",
          icon: "el-icon-s-order",
          noCache: false,
        },
      },
      {
        path: "records",
        name: "InventoryRecords",
        component: () => import("@/views/Business/InventoryRecords/index.vue"),
        meta: {
          title: "库存明细",
          icon: "el-icon-document",
          noCache: false,
        },
      },
      {
        path: "channel-stats",
        name: "ChannelStats",
        hidden: true,
        component: () => import("@/views/Business/ChannelStats/index.vue"),
        meta: {
          title: "库存管控",
          icon: "el-icon-data-analysis",
          noCache: false,
        },
      },
      {
        path: "shelf-zone",
        name: "ShelfZone",
        component: () => import("@/views/Business/ShelfZone/index.vue"),
        meta: {
          title: "货架分区",
          icon: "el-icon-place",
          noCache: false,
        },
      },
      {
        path: "live-label",
        name: "LiveLabel",
        hidden: true,
        component: () => import("@/views/Business/ShelfLabel/index.vue"),
        meta: {
          title: "直播标签",
          icon: "el-icon-video-camera",
          noCache: true,
        },
      },
      {
        path: "pattern-check",
        name: "PatternCheck",
        hidden: true,
        component: () => import("@/views/Business/PatternCheck/index.vue"),
        meta: {
          title: "花型核对",
          icon: "el-icon-picture-outline",
          noCache: false,
        },
      },
      {
        path: "product",
        name: "ProductList",
        component: () => import("@/views/Business/Product/index.vue"),
        meta: {
          title: "产品规格",
          icon: "el-icon-goods",
          noCache: false,
          permission: "system",
        },
      },
    ],
  },
  {
    path: "/analysis",
    component: Layout,
    redirect: "/analysis/alert",
    meta: {
      title: "数据分析",
      icon: "el-icon-data-analysis",
      requiresAuth: true,
      permission: "analytics",
    },
    children: [
      {
        path: "alert",
        name: "InventoryAlert",
        component: () => import("@/views/Business/InventoryAlert/index.vue"),
        meta: {
          title: "库存预警",
          icon: "el-icon-warning",
          noCache: false,
          permission: "analytics",
        },
      },
      {
        path: "insight",
        name: "InventoryInsight",
        hidden: true,
        component: () => import("@/views/Business/InventoryInsight/index.vue"),
        meta: {
          title: "库存洞察",
          icon: "el-icon-s-marketing",
          noCache: false,
          permission: "analytics",
        },
      },
      {
        path: "monitor",
        name: "InventoryMonitor",
        component: () => import("@/views/Business/InventoryMonitor/index.vue"),
        meta: {
          title: "周期监控",
          icon: "el-icon-data-analysis",
          noCache: false,
          permission: "analytics",
        },
      },
      {
        path: "cart-insight",
        name: "CartInsight",
        component: () => import("@/views/Business/CartInsight/index.vue"),
        meta: {
          title: "客户意向",
          icon: "el-icon-shopping-cart-2",
          noCache: false,
          permission: "analytics",
        },
      },
    ],
  },
  {
    path: "/pattern",
    component: Layout,
    redirect: "/pattern/list",
    meta: {
      title: "花型管理",
      icon: "el-icon-picture",
      requiresAuth: true,
      permission: "patterns:read",
    },
    children: [
      {
        path: "list",
        name: "PatternList",
        component: () => import("@/views/Pattern/PatternList/index.vue"),
        meta: {
          title: "花型列表",
          icon: "el-icon-picture",
          noCache: false,
        },
      },
      {
        path: "category",
        name: "CategoryManage",
        component: () => import("@/views/Business/Category/index.vue"),
        meta: {
          title: "分类管理",
          icon: "el-icon-s-operation",
          noCache: false,
          permission: "inventory:read",
        },
      },
    ],
  },
  {
    path: "/customer",
    hidden: true,
    component: Layout,
    redirect: "/customer/miniapp-users",
    meta: {
      title: "用户管理",
      icon: "el-icon-user-solid",
      requiresAuth: true,
      permission: "miniapp_users:read",
    },
    children: [
      {
        path: "miniapp-users",
        name: "MiniappUserList",
        component: () => import("@/views/MiniappUser/index.vue"),
        meta: {
          title: "小程序用户",
          icon: "el-icon-mobile-phone",
          noCache: false,
        },
      },
    ],
  },
  {
    hidden: true,
    path: "/my-tasks",
    component: Layout,
    meta: {
      title: "我的任务",
      icon: "el-icon-s-claim",
      requiresAuth: true,
      permission: "my_tasks",
    },
    children: [
      {
        path: "",
        name: "MyTasks",
        component: () => import("@/views/MyTasks/index.vue"),
        meta: {
          title: "我的任务",
          icon: "el-icon-s-claim",
          noCache: true,
        },
      },
    ],
  },
  {
    path: "/work",
    hidden: true,
    component: Layout,
    redirect: "/work/daily-task",
    meta: {
      title: "工作管理",
      icon: "el-icon-s-check",
      requiresAuth: true,
      permission: "daily_tasks:read",
    },
    children: [
      {
        path: "daily-task",
        name: "DailyTask",
        component: () => import("@/views/Business/DailyTask/index.vue"),
        meta: {
          title: "工作总览",
          icon: "el-icon-document-checked",
          noCache: false,
        },
      },
      {
        path: "shift-change",
        name: "ShiftChange",
        component: () => import("@/views/Business/ShiftChange/index.vue"),
        meta: {
          title: "换班记录",
          icon: "el-icon-sort",
          noCache: false,
          permission: "daily_tasks:read",
        },
      },
      {
        path: "template",
        name: "TaskTemplate",
        component: () => import("@/views/Business/TaskTemplate/index.vue"),
        meta: {
          title: "模板管理",
          icon: "el-icon-s-operation",
          noCache: false,
          permission: "daily_tasks:write",
        },
      },
      {
        path: "targets",
        name: "WorkTargets",
        component: () => import("@/views/Business/Targets/index.vue"),
        meta: {
          title: "目标管理",
          icon: "el-icon-data-analysis",
          noCache: false,
          permission: "daily_tasks:write",
        },
      },
      {
        path: "stats",
        name: "WorkStats",
        component: () => import("@/views/Business/WorkStats/index.vue"),
        meta: {
          title: "绩效统计",
          icon: "el-icon-trophy",
          noCache: false,
          permission: "daily_tasks:read",
        },
      },
      {
        path: "attendance",
        name: "AttendanceReport",
        component: () => import("@/views/Business/AttendanceReport/index.vue"),
        meta: {
          title: "考勤报表",
          icon: "el-icon-date",
          noCache: false,
          permission: "daily_tasks:read",
        },
      },
      {
        path: "attendance-settings",
        name: "AttendanceSettings",
        component: () => import("@/views/Business/AttendanceSettings/index.vue"),
        meta: {
          title: "打卡设置",
          icon: "el-icon-location-information",
          noCache: false,
          permission: "daily_tasks:write",
        },
      },
    ],
  },
  {
    path: "/user",
    component: Layout,
    redirect: "/user/profile",
    hidden: true,
    meta: { requiresAuth: true },
    children: [
      {
        path: "profile",
        name: "UserProfile",
        component: () => import("@/views/System/User/Profile.vue"),
        meta: {
          title: "个人中心",
          icon: "el-icon-user-solid",
          hidden: true,
        },
      },
    ],
  },
  {
    path: "/sales",
    hidden: true,
    component: Layout,
    redirect: "/sales/contacts",
    meta: {
      title: "客户管理",
      icon: "el-icon-user",
      requiresAuth: true,
      permission: "sales_contacts",
    },
    children: [
      {
        path: "contacts",
        name: "SalesContacts",
        component: () => import("@/views/Business/SalesContacts/index.vue"),
        meta: { title: "销售线索", icon: "el-icon-s-custom", noCache: true, permission: "sales_contacts" },
      },
      {
        path: "contacts/:id",
        name: "SalesContactDetail",
        hidden: true,
        component: () => import("@/views/Business/SalesContacts/Detail.vue"),
        meta: { title: "线索详情", noCache: true },
      },
    ],
  },
  {
    path: "/system",
    component: Layout,
    redirect: "/system/role",
    meta: {
      title: "系统管理",
      icon: "el-icon-setting",
      requiresAuth: true,
      permissions: ["system", "notices"],
    },
    children: [
      {
        path: "role",
        name: "Role",
        component: () => import("@/views/System/Role/index.vue"),
        meta: {
          title: "账号与权限",
          icon: "el-icon-s-custom",
          noCache: false,
          permission: "system",
        },
      },
      {
        path: "notice",
        name: "Notice",
        component: () => import("@/views/System/Notice/index.vue"),
        meta: {
          title: "公告管理",
          icon: "el-icon-bell",
          noCache: false,
          permission: "notices",
        },
      },
      {
        path: "log",
        name: "Log",
        component: () => import("@/views/System/Log/index.vue"),
        meta: {
          title: "操作日志",
          icon: "el-icon-document",
          noCache: false,
          permission: "system",
        },
      },
      {
        path: "feedback",
        name: "Feedback",
        hidden: true,
        component: () => import("@/views/System/Feedback/index.vue"),
        meta: {
          title: "用户留言",
          icon: "el-icon-chat-dot-round",
          noCache: false,
          permission: "system",
        },
      },
      {
        path: "order-rules",
        name: "OrderRules",
        component: () => import("@/views/Business/OrderRules/index.vue"),
        meta: {
          title: "订单规则",
          icon: "el-icon-s-operation",
          noCache: false,
          permission: "system",
        },
      },
      {
        path: "shipping-rate",
        name: "ShippingRate",
        component: () => import("@/views/Business/ShippingRate/index.vue"),
        meta: {
          title: "运费管理",
          icon: "el-icon-truck",
          noCache: false,
          permission: "system",
        },
      },
    ],
  },
  {
    path: "/404",
    name: "404",
    hidden: true,
    component: () => import("@/views/Error/404.vue"),
    meta: { title: "页面不存在", hidden: true },
  },
  {
    path: "*",
    redirect: "/404",
    hidden: true,
  },
];

const router = new VueRouter({
  mode: "hash",
  base: process.env.BASE_URL,
  routes,
});

// 路由守卫
router.beforeEach(async (to, from, next) => {
  try {
    // 设置页面标题
    if (to.meta.title) {
      document.title = `${to.meta.title} - 新迈管理后台`;
    }

    // 门禁验证：除 /sign 和标记 bypassGate 的路由外，所有页面都需要通过门禁
    const bypassGate = to.matched.some(r => r.meta?.bypassGate);
    if (to.path !== "/sign" && !bypassGate) {
      const gatePass = sessionStorage.getItem("admin_gate");
      if (!gatePass) {
        return next({ path: "/sign" });
      }
    }

    // 检查是否需要登录
    const token = localStorage.getItem("Admin-Token");
    const hasToken = !!token;
    const isTokenValid = hasToken ? isCurrentTokenValid() : false;

    // 如果token存在但无效，清除token并重置用户状态
    if (hasToken && !isTokenValid) {
      removeToken();
      store.dispatch("user/resetUser");
    }

    // token 有效但 userInfo 丢失（刷新后 store 清空），重新拉取用户信息
    if (isTokenValid && !store.state.user.userInfo?.role) {
      try {
        await store.dispatch("user/getUserInfo");
        store.commit("tabs/INIT_HOME");
      } catch (e) {
        // 拉取失败说明 token 已失效，清除并跳转登录
        store.dispatch("user/resetUser");
        return next({ path: "/login", query: { redirect: to.fullPath } });
      }
    }

    // 已登录用户不应再访问 /login
    if (isTokenValid && to.path === "/login") {
      // 检查是否有重定向参数，如果有则跳转到指定页面
      const redirect = to.query.redirect;
      if (redirect && redirect !== "/login" && redirect.startsWith("/") && !redirect.startsWith("//")) {
        return next({ path: redirect });
      }
      // staff 跳转到我的任务，其他跳转 dashboard
      const loginRole = store.state.user.userInfo?.role;
      if (loginRole === 'staff') {
        return next({ path: "/my-tasks" });
      }
      return next({ path: "/dashboard" });
    }

    // 需要登录的页面（在路由 meta 上标记 requiresAuth）
    const needAuth = to.matched.some(r => r.meta.requiresAuth);
    if (!isTokenValid && needAuth) {
      // 避免 redirect 指向 /login 自身导致无限循环
      const redirect = to.fullPath.startsWith("/login") ? undefined : to.fullPath;
      return next({
        path: "/login",
        query: redirect ? { redirect } : {},
      });
    }

    // 权限校验：从最深层路由向上找第一个有权限声明的 meta，只校验该层
    const userPermissions = store.getters['user/permissions'];
    const permMeta = [...to.matched].reverse().find(r => {
      const m = r.meta;
      return m && (m.permission || m.permissions || m.roles);
    })?.meta;
    let forbidden = false;
    if (permMeta) {
      if (permMeta.permission) forbidden = !userPermissions.has(permMeta.permission);
      else if (permMeta.permissions) forbidden = !permMeta.permissions.some(p => userPermissions.has(p));
      else if (permMeta.roles) forbidden = !store.state.user.userInfo?.role || !permMeta.roles.includes(store.state.user.userInfo.role);
    }
    if (forbidden) {
      const userRole = store.state.user.userInfo?.role;
      if (userRole === 'staff') {
        if (to.path === '/my-tasks') return next();
        return next({ path: "/my-tasks", replace: true });
      }
      // 有管理权限但无当前页权限，回首页，不跳 404
      if (to.path === '/dashboard') return next();
      return next({ path: "/dashboard", replace: true });
    }

    // 其他情况正常放行
    return next();
  } catch (e) {
    // 异常情况下也要确保只调用一次 next
    console.error("路由守卫错误:", e);
    return next();
  }
});

// 抑制 Vue Router 3 的 NavigationRedirected 误报
router.onError(err => {
  if (
    err.name === "NavigationDuplicated" ||
    err.message.includes("Redirected") ||
    err.message.includes("Navigation cancelled")
  ) {
    return;
  }
  console.error(err);
});

export default router;
